import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';

import {ConversionCriteria, QueryContext} from 'common/query-context';
import {ChartType, ProjectFeatureFlag, PropertyType, ResourceType} from 'common/report/constants';
import {FlowsResponse, FlowsSankeyResponse, FlowsTopPathsResponse} from 'common/report/funnels/models/api-types';
import {FlowsReport} from 'common/report/flows/models/report';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {FLOWS_CONVERSION} from 'common/query-builder/query-block/properties-menu/constants';
import {isCohortSegment, isOtherSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {
  FlowsQueryEventEntry,
  QueryEventEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {QueryOptionsSelectionDetail, SelectedQueryOptions} from 'common/query-builder/conversion-query-options/types';
import {createCohort, getCohortType, getDataGroupName, PopoverNode} from 'common/report/flows/visualization/util';
import {FunnelOrder} from 'common/report/funnels/models/constants';
import {addToast} from 'common/components/toast-area/toast-manager';
import {reporter} from 'common/report/error-reporting';
import {PopoverActionDetail} from 'common/report/flows/visualization/charts/chart-display';
import {SankeyNodeContextMenuAction} from 'common/report/flows/visualization/charts/chart-display/types';
import {removeByValue} from 'common/util';
import {TrackingEvent} from 'common/report/flows/tracking';
import {AdvancedQueryOptions, ChartType as FlowsChartType, FlowAnchorPosition} from 'common/report/flows/models/types';
import {AdvancedSettingsItem} from 'common/report/flows/visualization/advanced-settings';
import {Alignment} from 'common/widgets/sankey-chart/models/types';
import {
  OVERRIDE_STARTING_FORWARD_VALUE,
  STARTING_FORWARD_VALUE,
} from 'common/query-builder/query-entry-section/row-types/flows-entry';
import {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {renameEvent} from 'common/report/util/events';
import {propertiesUtils} from 'common/report/util/properties';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {openUserSidebar} from 'common/widgets/user-sidebar';
import {ReportName} from 'common/report/constants';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {SankeyBreakdown} from 'common/widgets/sankey-chart/models/sanky-breakdown';
import {SankeyNode} from 'common/widgets/sankey-chart/models/sankey-node';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';

export function isSankeyResponse(results: FlowsResponse): results is FlowsSankeyResponse {
  return Object.prototype.hasOwnProperty.call(results, `steps`);
}

export function isTopPathsResponse(results: FlowsResponse): results is FlowsTopPathsResponse {
  return Object.prototype.hasOwnProperty.call(results, `flows`);
}

export function isEmptyFlowsResult(results: FlowsResponse): boolean {
  if (isSankeyResponse(results)) {
    return !results.steps || results.steps.length === 0;
  } else if (isTopPathsResponse(results)) {
    return !results.flows || results.flows.length === 0;
  }

  throw new Error(`Invalid flows response.`);
}

// QB utils
export function getEventSectionType() {
  return SelectorTypeDefinitions.FlowsEvent;
}

export function getQueryEventsSection(report: FlowsReport): FlowsQueryEventEntry[] {
  return report.eventSelection.map((entry) => {
    return {
      ...entry,
      type: getEventSectionType(),
      funnelStep: report.containsFunnelBookmark(),
    };
  });
}

export function getUniqueEventNames(report: FlowsReport): string[] {
  return uniq(
    getQueryEventsSection(report).flatMap(({event}) => {
      if (Array.isArray(event)) {
        return event.map((evt) => evt.event.value);
      } else {
        return event.value;
      }
    }),
  );
}

export function getSelectedConversionOptions(report: FlowsReport): SelectedQueryOptions {
  return {
    conversionWindow: report.conversionWindow,
    countType: report.countType,
    funnelOrder: report.funnelOrder,
    holdingPropertyConstant: report.aggregateBy,
  };
}

export function getQbOtherOptions(): Array<PropertySectionItem<string>> {
  return [FLOWS_CONVERSION];
}

export function containsConversionGroupBy(groupBy: Segment[]): boolean {
  let containsGroupBy = false;
  if (groupBy) {
    containsGroupBy = groupBy.some((segment) => {
      return segment[`other`]?.value === FLOWS_CONVERSION.value;
    });
  }
  return containsGroupBy;
}

export function updateReportFromQBSelection(
  report: FlowsReport,
  selection: QueryBuilderUpdateDetail<FlowsQueryEventEntry>,
): FlowsReport {
  const updateParams: Partial<FlowsReport> = {...selection, events: selection.entries};

  if (selection.groupBy) {
    updateParams.alwaysBreakdown = containsConversionGroupBy(selection.groupBy);
  }

  return report.clone(updateParams);
}

export function updateReportFromConversionOptions(
  report: FlowsReport,
  selection: QueryOptionsSelectionDetail,
): FlowsReport {
  report = report.clone();

  if (selection.exclusionSteps) {
    report.update({exclusionSteps: selection.exclusionSteps});
  } else {
    let updates: Partial<FlowsReport> = {...selection};

    if (!report.isAnyOrderFunnel() && selection.funnelOrder === FunnelOrder.Any) {
      updates = {...updates, events: report.getDefaultAnyOrderSteps()};
    }

    if (selection.holdingPropertyConstant) {
      updates = {...updates, aggregateBy: selection.holdingPropertyConstant};
    }

    report.update(updates);
  }

  return report;
}

export function getReportQueryContext(
  report: FlowsReport,
  {defaultReport, originDashboard}: {defaultReport: FlowsReport; originDashboard: DashboardOrigin},
): QueryContext {
  if (isEqual(report.generateBookmark(), defaultReport.generateBookmark())) {
    return null;
  }

  const dateRange = report.dateRange.getAttributes();
  const dataGroupId = report.dataGroupId;

  const entries = getQueryEventsSection(report);
  const events: QueryEventEntry[] = entries.map((entry) => {
    return {
      type: ResourceType.Event,
      event: entry.event,
      filters: entry.filters,
      filtersOperator: entry.filtersOperator,
    };
  });

  const segments = report.groupBy;
  const filters = report.filters as PropertyFilter[];

  const conversionCriteria: ConversionCriteria = {
    conversionWindow: report.conversionWindow,
    countType: report.countType,
    exclusionSteps: report.exclusionSteps,
    holdingPropertyConstant: report.aggregateBy,
  };

  return {
    bookmarkType: BookmarkType.Flows,
    dataGroupId,
    dateRange,
    querySamplingEnabled: report.querySampling ?? false,
    events,
    ...(originDashboard && {originDashboard}),
    ...(segments && {segments: segments.filter((segment) => !isOtherSegment(segment))}),
    ...(filters && {filters}),
    ...{conversionCriteria},
  };
}

export function getReportFromQueryContext(
  queryContext: QueryContext,
  project: CurrentProject | ProjectDetails,
): FlowsReport {
  let segments =
    queryContext.segments && queryContext.segments.filter((segment) => isCohortSegment(segment)).slice(0, 1);

  if (segments?.length === 0 && queryContext.events?.length > 1) {
    segments = [
      {
        other: {
          resourceType: ResourceType.Other,
          propertyType: PropertyType.String,
          propertyName: `$conversion`,
          propertyDefaultType: PropertyType.String,
          label: `Conversion`,
          value: `$conversion`,
        },
      },
    ];
  }

  const numForwardSteps = project?.hasFeatureFlag(ProjectFeatureFlag.FlowsLessDefaultSteps)
    ? OVERRIDE_STARTING_FORWARD_VALUE
    : STARTING_FORWARD_VALUE;

  let events = queryContext.events && queryContext.events.map((event) => ({...event, forward: numForwardSteps}));

  if (queryContext.viewAsFlowsContext) {
    events =
      queryContext.events &&
      queryContext.events.map((event, index) => ({
        ...event,
        forward: queryContext.viewAsFlowsContext.funnelStep === index ? numForwardSteps : 0,
      }));
  }

  return new FlowsReport({
    ...(queryContext.querySamplingEnabled && {querySampling: true}),
    ...(queryContext.dataGroupId && {dataGroupId: queryContext.dataGroupId}),
    ...(queryContext.dateRange && {dateRange: queryContext.dateRange}),
    ...(queryContext.events && {events}),
    ...(queryContext.segments && {groupBy: segments}),
    ...(queryContext.filters && {filters: queryContext.filters}),
    ...(queryContext.conversionCriteria && {
      ...queryContext.conversionCriteria,
      aggregateBy: queryContext.conversionCriteria.holdingPropertyConstant,
    }),
  });
}

// util extracted to centralize logic with fast-edit version
// TODO: move this into FlowsVisualization when replacing flows app
export function handleFlowsPopoverAction({
  detail,
  customEvents,
  report,
  updateFn,
  trackFn,
}: {
  detail: PopoverActionDetail;
  customEvents: CustomEventDefinition[];
  report: FlowsReport;
  updateFn: (params: Partial<FlowsReport>) => void;
  trackFn: (evName: string, props: any) => void;
}) {
  const {action, node, isCustomEvent, stepLabel, breakdown, property} = detail;
  const eventName = node.event;

  switch (action) {
    case SankeyNodeContextMenuAction.ExpandByProperty: {
      let event = {value: eventName, label: null};

      if (isCustomEvent) {
        const matchingCustomEvent = customEvents.find((event) => event.name === eventName);
        if (matchingCustomEvent) {
          event = {value: `$custom_event:${matchingCustomEvent.id}`, label: eventName};
        }
      }

      updateFn({segments: [...report.segments, {event, property}]});

      trackFn(TrackingEvent.ADD_BREAK_DOWN, {
        'Event name': event,
        'Property name': property.label,
        'Property value': property.value,
        'Property data type': property.type,
      });

      const undoFunction = () =>
        updateFn({
          segments: report.segments.filter(
            (segment) => segment.event.value !== event.value || segment.property.value !== property.value,
          ),
        });
      addToast({
        message: `Segmented "${renameEvent(event.value)}" by "${propertiesUtils.renameProperty(
          property.value,
          ResourceType.Event,
        )}"`,
        cta: `undo`,
        ctaCallback: undoFunction,
      });
      break;
    }
    case SankeyNodeContextMenuAction.HideEvent: {
      let undoFunction;
      const {hiddenEvents, visibleEvents} = report;

      // if report visibleEvents is set, the `hide` action
      // must come from a node in the visibleEvents set, it must
      // be removed from that set instead of hiddenEvents
      if (visibleEvents.length) {
        updateFn({visibleEvents: removeByValue(visibleEvents, eventName)});
        undoFunction = () => updateFn({visibleEvents: [...report.visibleEvents, eventName]});
      } else {
        updateFn({hiddenEvents: [...hiddenEvents, eventName]});
        undoFunction = () => updateFn({hiddenEvents: removeByValue(report.hiddenEvents, eventName)});
      }

      trackFn(TrackingEvent.HIDE_EVENT, {
        'Event name': eventName,
      });

      addToast({
        message: `"${eventName}" is hidden`,
        cta: `undo`,
        ctaCallback: undoFunction,
      });
      break;
    }
    case SankeyNodeContextMenuAction.AddCardinality: {
      updateFn({cardinality: report.cardinality + 1});
      break;
    }
    case SankeyNodeContextMenuAction.RemoveCardinality: {
      updateFn({cardinality: report.cardinality - 1});
      break;
    }
    case SankeyNodeContextMenuAction.ViewUsers: {
      const cohortDetail = getCohortDetail({
        node,
        breakdown,
        report,
      });
      const eventString: string = node instanceof SankeyNode ? node.eventString : eventName;
      openUserSidebar(
        {
          'hide-cohort-functionality': true,
          'include-all-users': true,
          'report-name': ReportName.Flows,
          segments: [`Step ${stepLabel}: ${eventString}`, ...node.segments.map((segment) => segment.value)],
          'segment-label': getSegmentLabel(breakdown),
          'data-group-name': getDataGroupName({activeDataGroupId: report.dataGroupId, plural: false}),
          color: breakdown.color.primary,
        },
        {
          cohort: cohortDetail.cohort,
          cohortDetails: [cohortDetail],
        },
      );
      break;
    }
    default:
      reporter.error(`Unknown context menu action:`, action);
      break;
  }
}

function getCohortDetail({
  node,
  breakdown,
  report,
}: {
  node: PopoverNode;
  breakdown: SankeyBreakdown;
  report: FlowsReport;
}): CohortDetail {
  return {
    warning: ``,
    label: ``,
    type: getCohortType(node),
    cohort: createCohort({
      node,
      breakdown,
      bookmark: report.generateBookmark(),
    }),
  };
}

function getSegmentLabel(breakdown) {
  // For the simple case where we have a single cohort, like for Conversion, display its name.
  // Cohort segments have no label and have a single value, which is the name of the Cohort.
  if (
    breakdown &&
    breakdown.segments.length === 1 &&
    breakdown.segments[0].label === `` &&
    breakdown.segments[0].values.length === 1
  ) {
    let cohortName = breakdown.segments[0].values[0];
    if (cohortName === `Converted`) {
      return `who converted`;
    }
    if (cohortName === `Did Not Convert`) {
      return `who did not convert`;
    }

    const notInPrefix = `Not In `;
    if (cohortName.substring(0, notInPrefix.length) === `Not In `) {
      cohortName = cohortName.slice(notInPrefix.length);
      return `not in cohort: ${cohortName}`;
    }
    return `in cohort: ${cohortName}`;
  }

  // For all other cases, default to "users in segment".
  // Attempting to add multiple cohorts to the string would make it too long and hard to understand.
  return `in segment`;
}

export function getAnchorPositionForChartType(chartType: ChartType): FlowAnchorPosition {
  return chartType === ChartType.FlowsTopPaths ? FlowAnchorPosition.Funnel : FlowAnchorPosition.Step;
}

export function getAdvancedQueryOptionItems({
  report,
  percentilesEnabled,
  isDev,
}: {
  report: FlowsReport;
  percentilesEnabled: boolean;
  isDev: boolean;
}): AdvancedSettingsItem[] {
  const items: AdvancedSettingsItem[] = [
    {option: AdvancedQueryOptions.CollapseRepeated, toggled: report.collapseRepeated},
    {option: AdvancedQueryOptions.ShowCustomEvents, toggled: report.showCustomEvents},
  ];
  const percentilesFeatureEnabled = isDev || percentilesEnabled;

  // TODO jayant: some customers have saved bookmarks with this - decide whether to GA this feature or not
  if (percentilesFeatureEnabled) {
    items.push({option: AdvancedQueryOptions.TimePercentiles, toggled: report.timePercentiles.length > 0});
  }

  // TODO jayant: decide whether to GA this feature or not
  if (isDev) {
    items.push({option: AdvancedQueryOptions.AlwaysBreakdown, toggled: report.alwaysBreakdown});
  }

  return items;
}

export function updateReportFromAdvancedSettings(report: FlowsReport, item: AdvancedSettingsItem) {
  const {option, toggled} = item;
  report = report.clone();

  switch (option) {
    case AdvancedQueryOptions.CollapseRepeated:
      report.update({collapseRepeated: toggled});
      break;
    case AdvancedQueryOptions.ShowCustomEvents:
      report.update({showCustomEvents: toggled});
      break;
    case AdvancedQueryOptions.TimePercentiles:
      report.update({timePercentiles: toggled ? [50.0] : []});
      break;
    case AdvancedQueryOptions.AlwaysBreakdown:
      report.update({alwaysBreakdown: toggled});
      break;
  }

  return report;
}

export function getAlignmentToggleDisabledText(report: FlowsReport): string | null {
  if (report.chartType === FlowsChartType.Paths) {
    return `Alignment not available on Top Paths view.`;
  }

  if (!report.alignment || report.alignment.length <= 2) {
    return `Alignment available with 2 or more anchor steps.`;
  }

  return null;
}

export function updateReportAlignment(report: FlowsReport, toggledAlignment: Alignment): FlowsReport {
  report = report.clone();
  const alignment = [...report.alignment];

  const originalAlignment = report.getOverallAlignment();
  if (toggledAlignment !== originalAlignment) {
    for (let i = 1; i < alignment.length - 1; i++) {
      alignment[i] = toggledAlignment;
    }
  }

  report.alignment = alignment;
  return report;
}

export function updateReportHiddenEvents(report: FlowsReport, queryBlockDetail: TODO) {
  // TODO: type this in multi-events-menu
  const {action, selected, unselected} = queryBlockDetail;

  report = report.clone();

  let hiddenEvents = [];
  let visibleEvents = [];

  if (action === `show`) {
    visibleEvents = unselected;
  } else if (action === `hide`) {
    hiddenEvents = selected;
  }

  report.update({
    visibleEvents,
    hiddenEvents,
  });

  return report;
}
